/** Printed rarity of one expansion, after Short Print folds into Common. */

export const PRINTED_RARITIES = [
  'common',
  'rare',
  'super',
  'ultra',
  'secret',
] as const

export type PrintedRarity = (typeof PRINTED_RARITIES)[number]

/** Higher number = higher rarity. Secret sits above Ultra. */
export const PRINTED_RARITY_RANK: Record<PrintedRarity, number> = {
  common: 0,
  rare: 1,
  super: 2,
  ultra: 3,
  secret: 4,
}

/**
 * Official set_rarity label. Super Short Print stays Super. Short Print
 * stays Common, the way the 2002 box counted those cards.
 */
export function parsePrintedRarity(raw: string | undefined): PrintedRarity {
  const text = (raw || '').toLowerCase()
  if (text.includes('secret')) return 'secret'
  if (text.includes('ultra')) return 'ultra'
  if (text.includes('super')) return 'super'
  if (text.includes('short')) return 'common'
  if (text.includes('rare')) return 'rare'
  return 'common'
}

/**
 * One printing per prefix. A numeric code (LOB-027) wins over a lettered one.
 */
export function printingsFromSets(
  sets: readonly { set_code?: string; set_rarity?: string }[] | undefined,
  prefixes: readonly string[],
): { set: string; rarity: PrintedRarity }[] {
  const list = sets ?? []
  const out: { set: string; rarity: PrintedRarity }[] = []
  for (const prefix of prefixes) {
    const numeric = new RegExp(`^${prefix}-\\d`, 'i')
    const any = new RegExp(`^${prefix}-`, 'i')
    const hit =
      list.find((row) => numeric.test(row.set_code || '')) ??
      list.find((row) => any.test(row.set_code || ''))
    if (!hit) continue
    out.push({ set: prefix, rarity: parsePrintedRarity(hit.set_rarity) })
  }
  return out
}

/** Highest rarity among the printings this card has in the legal sets. */
export function highestPrintedRarity(
  rarities: readonly PrintedRarity[],
): PrintedRarity {
  let best: PrintedRarity = 'common'
  for (const rarity of rarities) {
    if (PRINTED_RARITY_RANK[rarity] > PRINTED_RARITY_RANK[best]) best = rarity
  }
  return best
}
